/**
 * Consolidates raw trip slices into logical trips.
 *
 * Heavy stop-and-go traffic (e.g. Bengaluru) makes a single journey arrive as
 * many short trip rows: every ACC blip or telemetry gap used to open a new
 * slice. The rule implemented here (mirroring the backend's
 * `Gt06ReportService.consolidateIntoTrips`, config
 * `gt06.reports.trip-stop-split-minutes`): two consecutive slices of the same
 * device belong to the SAME logical trip unless the stop between them — end of
 * one slice to start of the next — persists for at least the threshold
 * (default 30 minutes). This is a defensive client-side pass: once the backend
 * merge is deployed it becomes a no-op, but it keeps the UI clean against
 * older API versions.
 */

export const DEFAULT_STOP_SPLIT_MINUTES = 30;

const ms = (t) => (t == null ? NaN : new Date(t).getTime());

/** Merge two summary rows into one logical trip, recomputing aggregates. */
function mergeRows(a, b) {
  const distanceKm = (a.distanceKm || 0) + (b.distanceKm || 0);

  // Reconstruct implied moving hours per slice (distance / avg) so the merged
  // average stays moving-time-based like the backend's, instead of being
  // diluted by the absorbed stop.
  const movingHours =
    (a.avgSpeedKmph > 0 ? (a.distanceKm || 0) / a.avgSpeedKmph : 0) +
    (b.avgSpeedKmph > 0 ? (b.distanceKm || 0) / b.avgSpeedKmph : 0);

  const gapMinutes = Math.max(0, (ms(b.startTime) - ms(a.endTime)) / 60000);

  const alerts = {...(a.alerts || {})};
  for (const [k, v] of Object.entries(b.alerts || {})) {
    alerts[k] = (alerts[k] || 0) + v;
  }

  return {
    ...a,
    endTime: b.endTime,
    durationMinutes: Math.max(0, Math.round((ms(b.endTime) - ms(a.startTime)) / 60000)),
    distanceKm: Math.round(distanceKm * 100) / 100,
    maxSpeedKmph: Math.max(a.maxSpeedKmph || 0, b.maxSpeedKmph || 0),
    avgSpeedKmph: movingHours > 0 ? Math.round((distanceKm / movingHours) * 100) / 100 : 0,
    idleMinutes: Math.round(((a.idleMinutes || 0) + (b.idleMinutes || 0)) * 100) / 100,
    // The absorbed stop is stop time — keep the accounting truthful.
    stopMinutes:
      Math.round(((a.stopMinutes || 0) + (b.stopMinutes || 0) + gapMinutes) * 100) / 100,
    pointCount: (a.pointCount || 0) + (b.pointCount || 0),
    alerts,
  };
}

/**
 * @param {Array} trips trip-summary rows ({assetUnitId, startTime, endTime, …})
 * @param {number} thresholdMinutes minimum persistent stop that splits trips
 * @returns {Array} consolidated logical trips, ordered like the input groups
 */
export function consolidateTrips(trips, thresholdMinutes = DEFAULT_STOP_SPLIT_MINUTES) {
  if (!Array.isArray(trips) || trips.length <= 1) return trips || [];

  // Group per device — never merge trips across two different vehicles.
  const byDevice = new Map();
  for (const t of trips) {
    const k = t.assetUnitId || t.deviceId || "?";
    if (!byDevice.has(k)) byDevice.set(k, []);
    byDevice.get(k).push(t);
  }

  const thresholdMs = thresholdMinutes * 60000;
  const out = [];

  for (const [device, slices] of byDevice) {
    slices.sort((x, y) => ms(x.startTime) - ms(y.startTime));

    const merged = slices.reduce((acc, slice) => {
      if (acc.length === 0) return [slice];
      const prev = acc[acc.length - 1];
      const gapMs = ms(slice.startTime) - ms(prev.endTime);

      if (Number.isFinite(gapMs) && gapMs < thresholdMs) {
        console.debug(
          `[tripConsolidation] ${device}: absorbed ${(gapMs / 60000).toFixed(1)}-min stop ` +
            `(${prev.endTime} -> ${slice.startTime}) into the same trip`
        );
        acc[acc.length - 1] = mergeRows(prev, slice);
      } else {
        acc.push(slice);
      }
      return acc;
    }, []);

    out.push(...merged);
  }

  console.info(
    `Consolidated ${trips.length} raw trip slices into ${out.length} logical trips ` +
      `based on ${thresholdMinutes}-min stop threshold.`
  );
  return out;
}

/** Merge two trip-route sessions (with points/alertMarkers) into one. */
function mergeRouteSessions(a, b) {
  return {
    ...mergeRows(a, b),
    points: [...(a.points || []), ...(b.points || [])],
    alertMarkers: [...(a.alertMarkers || []), ...(b.alertMarkers || [])],
  };
}

/**
 * Trip Route variant: sessions arrive time-ordered for a single device, each
 * carrying its polyline `points` and `alertMarkers`. Same 30-minute rule —
 * a 1-minute crawl gap at a signal must not produce "Trip 2"; the merged
 * session draws as one continuous route.
 */
export function consolidateRouteSessions(sessions, thresholdMinutes = DEFAULT_STOP_SPLIT_MINUTES) {
  if (!Array.isArray(sessions) || sessions.length <= 1) return sessions || [];

  const thresholdMs = thresholdMinutes * 60000;
  const out = sessions.reduce((acc, session) => {
    if (acc.length === 0) return [session];
    const prev = acc[acc.length - 1];
    const gapMs = ms(session.startTime) - ms(prev.endTime);

    if (Number.isFinite(gapMs) && gapMs < thresholdMs) {
      console.debug(
        `[tripConsolidation] route: absorbed ${(gapMs / 60000).toFixed(1)}-min stop ` +
          `(${prev.endTime} -> ${session.startTime}) into the same trip`
      );
      acc[acc.length - 1] = mergeRouteSessions(prev, session);
    } else {
      acc.push(session);
    }
    return acc;
  }, []);

  console.info(
    `Consolidated ${sessions.length} raw route sessions into ${out.length} logical trips ` +
      `based on ${thresholdMinutes}-min stop threshold.`
  );
  return out;
}
